import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronRight, MapPin, Loader2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { api } from '../../lib/api';
import { setCachedStations, getCachedStations } from '../../lib/offline';
import { useAuth } from '../../context/AuthContext';
import { OwnTrackingNotice } from '../../components/OwnTrackingNotice';

const StationList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isTM = user?.role === 'territory_manager';
  const [allStations, setAllStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [brand, setBrand] = useState('all');

  useEffect(() => {
    api.get('/stations')
      .then(r => { setAllStations(r.data); setCachedStations(r.data); })
      .catch(async () => setAllStations(await getCachedStations()))
      .finally(() => setLoading(false));
  }, []);

  // TM y agentes: solo estaciones gestionadas (los markers se ven en el mapa al enfocar la estación)
  const stations = useMemo(() => allStations.filter(s => !s.tm_marker_only), [allStations]);

  const filtered = useMemo(() => stations.filter(s => {
    const mq = !q || s.name.toLowerCase().includes(q.toLowerCase()) || s.address.toLowerCase().includes(q.toLowerCase());
    const mb = brand === 'all' || s.brand === brand;
    return mq && mb;
  }), [q, brand, stations]);

  // Chips de marca dinámicos: solo las marcas que este usuario realmente ve
  const brands = useMemo(() => Array.from(new Set(stations.map(s => s.brand).filter(Boolean))).sort(), [stations]);

  return (
    <div className="px-5 py-5">
      <h2 className="font-serif text-[28px] leading-tight tracking-tight mb-1">Estaciones</h2>
      <p className="text-[13px] text-[#5a5a5a] mb-3">{stations.length} estaciones registradas</p>

      {user?.role === 'capturer' && <OwnTrackingNotice />}

      <div className="relative mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8579]" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nombre o dirección…" className="pl-9 h-11 bg-[#f3f0e9] border-[#e8e5de] focus:border-[#1c1c1c] rounded-full text-[13px]" />
      </div>

      {brands.length > 1 && (
        <div className="flex gap-2 mb-5 overflow-x-auto pb-1 -mx-5 px-5" data-testid="brand-chips">
          <button onClick={() => setBrand('all')} className={`shrink-0 px-3.5 h-8 text-[12px] rounded-full border ${brand==='all'?'bg-[#1c1c1c] text-[#f8f6f1] border-[#1c1c1c]':'bg-white border-[#e8e5de] text-[#5a5a5a]'}`}>Todas</button>
          {brands.map(b => (
            <button key={b} onClick={() => setBrand(b)} className={`shrink-0 px-3.5 h-8 text-[12px] rounded-full border ${brand===b?'bg-[#1c1c1c] text-[#f8f6f1] border-[#1c1c1c]':'bg-white border-[#e8e5de] text-[#5a5a5a]'}`}>{b}</button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#8a8579]"/></div>
      ) : (
        <div className="space-y-2">
          {filtered.map(s => (
            <button key={s.id} onClick={() => navigate(`/app/station/${s.id}`)} className="w-full text-left bg-white border border-[#e8e5de] hover:border-[#1c1c1c]/40 rounded-xl p-4 flex items-center gap-3 transition">
              <div className="w-10 h-10 rounded-lg bg-[#f3f0e9] flex items-center justify-center shrink-0"><MapPin size={16}/></div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] tracking-wider uppercase text-[#8a8579] flex items-center gap-1.5">
                  {s.brand}
                  {(!s.is_own || s.trackable_as_marker) && <span className="text-[8.5px] px-1.5 py-px rounded-full bg-[#fee2e2] text-[#991b1b] tracking-wider">MARKER</span>}
                </p>
                <h4 className="text-[14px] font-medium tracking-tight truncate">{s.name}</h4>
                <p className="text-[12px] text-[#8a8579] truncate">{s.address}</p>
              </div>
              <ChevronRight size={16} className="text-[#8a8579] shrink-0" />
            </button>
          ))}
          {filtered.length === 0 && <p className="text-center text-[13px] text-[#8a8579] py-8">No se encontraron estaciones</p>}
        </div>
      )}
    </div>
  );
};

export default StationList;
